import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Ticket, Plus, Minus, ShoppingCart } from "lucide-react";

interface PriceTier {
  id: string;
  name: string;
  price: number;
  description: string | null;
  max_quantity: number | null;
}

interface Extra {
  id: string;
  name: string;
  price: number;
  description: string | null;
  max_per_ticket: number | null;
}

interface TicketSelection {
  tierId: string;
  tierName: string;
  tierPrice: number;
  attendeeName: string;
  extras: { extraId: string; quantity: number }[];
}

interface TicketPurchaseDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  eventTitle: string;
  ptaId: string;
  namedTicketsRequired: boolean;
  ticketingEnabled: boolean;
  onPurchaseComplete?: () => void;
}

export function TicketPurchaseDialog({
  open,
  onOpenChange,
  eventId,
  eventTitle,
  ptaId,
  namedTicketsRequired,
  ticketingEnabled,
  onPurchaseComplete,
}: TicketPurchaseDialogProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [priceTiers, setPriceTiers] = useState<PriceTier[]>([]);
  const [extras, setExtras] = useState<Extra[]>([]);

  // Form state
  const [buyerName, setBuyerName] = useState("");
  const [buyerEmail, setBuyerEmail] = useState("");
  const [buyerPhone, setBuyerPhone] = useState("");
  const [tickets, setTickets] = useState<TicketSelection[]>([]);

  useEffect(() => {
    if (open) {
      fetchData();
      if (user) {
        setBuyerEmail(user.email || "");
      }
    }
  }, [open, eventId, user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: tiers } = await supabase
        .from("ticket_price_tiers")
        .select("*")
        .eq("event_id", eventId)
        .order("display_order");

      setPriceTiers(tiers || []);

      const { data: extrasData } = await supabase
        .from("ticket_extras")
        .select("*")
        .eq("event_id", eventId)
        .order("display_order");

      setExtras(extrasData || []);
    } catch (error) {
      console.error("Error fetching ticket data:", error);
    } finally {
      setLoading(false);
    }
  };

  const addTicket = (tier: PriceTier) => {
    const newTicket: TicketSelection = {
      tierId: tier.id,
      tierName: tier.name,
      tierPrice: tier.price,
      attendeeName: "",
      extras: [],
    };
    setTickets([...tickets, newTicket]);
  };

  const removeTicket = (index: number) => {
    setTickets(tickets.filter((_, i) => i !== index));
  };

  const updateAttendeeName = (index: number, name: string) => {
    const updated = [...tickets];
    updated[index].attendeeName = name;
    setTickets(updated);
  };

  const updateTicketExtra = (ticketIndex: number, extraId: string, quantity: number) => {
    const updated = [...tickets];
    const existingExtra = updated[ticketIndex].extras.find((e) => e.extraId === extraId);

    if (existingExtra) {
      if (quantity === 0) {
        updated[ticketIndex].extras = updated[ticketIndex].extras.filter(
          (e) => e.extraId !== extraId
        );
      } else {
        existingExtra.quantity = quantity;
      }
    } else if (quantity > 0) {
      updated[ticketIndex].extras.push({ extraId, quantity });
    }

    setTickets(updated);
  };

  const calculateTotal = () => {
    let total = 0;
    for (const ticket of tickets) {
      total += ticket.tierPrice;
      for (const extra of ticket.extras) {
        const extraInfo = extras.find((e) => e.id === extra.extraId);
        if (extraInfo) {
          total += extraInfo.price * extra.quantity;
        }
      }
    }
    return total;
  };

  const generateQrCode = () => {
    const timestamp = Date.now().toString(36);
    const random = Math.random().toString(36).substring(2, 10);
    return `TKT-${timestamp}-${random}`.toUpperCase();
  };

  const handleSubmit = async () => {
    if (!buyerName.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!buyerEmail.trim()) {
      toast.error("Please enter your email");
      return;
    }
    if (tickets.length === 0) {
      toast.error("Please select at least one ticket");
      return;
    }
    if (namedTicketsRequired) {
      const missingNames = tickets.some((t) => !t.attendeeName.trim());
      if (missingNames) {
        toast.error("Please enter a name for each ticket");
        return;
      }
    }

    setSubmitting(true);
    try {
      const qrCode = generateQrCode();
      const totalAmount = calculateTotal();

      // Create the purchase record
      const { data: purchase, error: purchaseError } = await supabase
        .from("ticket_purchases")
        .insert({
          event_id: eventId,
          pta_id: ptaId,
          buyer_user_id: user?.id || null,
          buyer_name: buyerName,
          buyer_email: buyerEmail,
          buyer_phone: buyerPhone || null,
          total_amount: totalAmount,
          payment_status: totalAmount === 0 ? "paid" : "pending",
          qr_code: qrCode,
        })
        .select()
        .single();

      if (purchaseError) throw purchaseError;

      // Create ticket items
      for (const ticket of tickets) {
        const { data: item, error: itemError } = await supabase
          .from("ticket_purchase_items")
          .insert({
            purchase_id: purchase.id,
            price_tier_id: ticket.tierId,
            attendee_name: namedTicketsRequired ? ticket.attendeeName : null,
          })
          .select()
          .single();

        if (itemError) throw itemError;

        for (const extra of ticket.extras) {
          const { error: extraError } = await supabase
            .from("ticket_purchase_item_extras")
            .insert({
              purchase_item_id: item.id,
              extra_id: extra.extraId,
              quantity: extra.quantity,
            });

          if (extraError) throw extraError;
        }
      }

      // Update tickets_sold count on event
      const { data: eventData } = await supabase
        .from("events")
        .select("tickets_sold")
        .eq("id", eventId)
        .single();
      
      await supabase
        .from("events")
        .update({ tickets_sold: (eventData?.tickets_sold || 0) + tickets.length })
        .eq("id", eventId);

      // For paid tickets, redirect to checkout
      if (totalAmount > 0) {
        // Detect payment provider
        const { data: provider } = await supabase
          .rpc('get_pta_payment_provider', { p_pta_id: ptaId });

        if (provider === 'mollie') {
          const { data: mollieData, error: mollieError } = await supabase.functions.invoke('mollie-create-payment', {
            body: {
              ptaId,
              paymentType: 'ticket_purchase',
              amount: totalAmount,
              currency: 'GBP',
              description: `Event Tickets: ${eventTitle}`,
              referenceId: purchase.id,
              metadata: { buyerName, buyerEmail, eventId },
            }
          });

          if (mollieError) throw mollieError;
          if (mollieData?.checkoutUrl) {
            window.location.href = mollieData.checkoutUrl;
            return;
          }
        } else if (provider === 'stripe') {
          const { data: stripeData, error: stripeError } = await supabase.functions.invoke('create-ticket-checkout', {
            body: { purchaseId: purchase.id }
          });

          if (stripeError) throw stripeError;
          if (stripeData?.url) {
            window.location.href = stripeData.url;
            return;
          }
        } else {
          // No payment provider - mark as pending and notify
          toast.warning("Tickets reserved but no online payment is configured. Please contact the PTA to arrange payment.");
        }
      }

      // For free tickets or fallback
      // Send confirmation email
      try {
        await supabase.functions.invoke("send-ticket-confirmation", {
          body: { purchaseId: purchase.id },
        });
      } catch (emailError) {
        console.error("Failed to send confirmation email:", emailError);
      }

      toast.success("Tickets purchased successfully! Check your email for confirmation.");

      setTickets([]);
      setBuyerName("");
      setBuyerPhone("");
      onOpenChange(false);
      onPurchaseComplete?.();
    } catch (error: any) {
      console.error("Error purchasing tickets:", error);
      toast.error(error.message || "Failed to purchase tickets");
    } finally {
      setSubmitting(false);
    }
  };

  const total = calculateTotal();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Purchase Tickets</DialogTitle>
          <DialogDescription>{eventTitle}</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Buyer Information */}
            <div className="space-y-4">
              <h3 className="font-semibold">Your Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="buyerName">Name *</Label>
                  <Input
                    id="buyerName"
                    value={buyerName}
                    onChange={(e) => setBuyerName(e.target.value)}
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <Label htmlFor="buyerEmail">Email *</Label>
                  <Input
                    id="buyerEmail"
                    type="email"
                    value={buyerEmail}
                    onChange={(e) => setBuyerEmail(e.target.value)}
                    placeholder="your@email.com"
                  />
                </div>
                <div className="md:col-span-2">
                  <Label htmlFor="buyerPhone">Phone (optional)</Label>
                  <Input
                    id="buyerPhone"
                    type="tel"
                    value={buyerPhone}
                    onChange={(e) => setBuyerPhone(e.target.value)}
                    placeholder="Your phone number"
                  />
                </div>
              </div>
            </div>

            {/* Ticket Selection */}
            <div className="space-y-4">
              <h3 className="font-semibold">Select Tickets</h3>
              <div className="grid gap-3">
                {priceTiers.map((tier) => (
                  <Card key={tier.id}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">{tier.name}</div>
                          {tier.description && (
                            <div className="text-sm text-muted-foreground">
                              {tier.description}
                            </div>
                          )}
                          <div className="text-lg font-bold">
                            £{tier.price.toFixed(2)}
                          </div>
                        </div>
                        <Button onClick={() => addTicket(tier)} size="sm">
                          <Plus className="h-4 w-4 mr-1" />
                          Add
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>

            {/* Selected Tickets */}
            {tickets.length > 0 && (
              <div className="space-y-4">
                <h3 className="font-semibold">Your Tickets ({tickets.length})</h3>
                <div className="space-y-4">
                  {tickets.map((ticket, index) => (
                    <Card key={index} className="relative">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Ticket className="h-4 w-4" />
                            <span className="font-medium">
                              Ticket {index + 1}: {ticket.tierName}
                            </span>
                            <span className="text-muted-foreground">
                              £{ticket.tierPrice.toFixed(2)}
                            </span>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeTicket(index)}
                            className="h-8 w-8 p-0 text-destructive"
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                        </div>

                        {namedTicketsRequired && (
                          <div className="mb-3">
                            <Label>Attendee Name *</Label>
                            <Input
                              value={ticket.attendeeName}
                              onChange={(e) =>
                                updateAttendeeName(index, e.target.value)
                              }
                              placeholder="Name of person attending"
                            />
                          </div>
                        )}

                        {extras.length > 0 && (
                          <div className="space-y-2">
                            <Label className="text-sm">Extras</Label>
                            {extras.map((extra) => {
                              const selected = ticket.extras.find(
                                (e) => e.extraId === extra.id
                              );
                              const quantity = selected?.quantity || 0;
                              const maxQty = extra.max_per_ticket || 10;

                              return (
                                <div
                                  key={extra.id}
                                  className="flex items-center justify-between py-2 border-b last:border-0"
                                >
                                  <div>
                                    <div className="text-sm">{extra.name}</div>
                                    <div className="text-xs text-muted-foreground">
                                      +£{extra.price.toFixed(2)}
                                    </div>
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      className="h-7 w-7"
                                      onClick={() =>
                                        updateTicketExtra(
                                          index,
                                          extra.id,
                                          Math.max(0, quantity - 1)
                                        )
                                      }
                                      disabled={quantity === 0}
                                    >
                                      <Minus className="h-3 w-3" />
                                    </Button>
                                    <span className="w-6 text-center">
                                      {quantity}
                                    </span>
                                    <Button
                                      variant="outline"
                                      size="icon"
                                      className="h-7 w-7"
                                      onClick={() =>
                                        updateTicketExtra(
                                          index,
                                          extra.id,
                                          Math.min(maxQty, quantity + 1)
                                        )
                                      }
                                      disabled={quantity >= maxQty}
                                    >
                                      <Plus className="h-3 w-3" />
                                    </Button>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Total */}
            {tickets.length > 0 && (
              <div className="bg-muted p-4 rounded-lg">
                <div className="flex items-center justify-between text-lg font-bold">
                  <span>Total</span>
                  <span>£{total.toFixed(2)}</span>
                </div>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={submitting || tickets.length === 0}
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <ShoppingCart className="h-4 w-4 mr-2" />
            )}
            {total === 0 ? "Reserve Tickets" : `Pay £${total.toFixed(2)}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
